/**
 * ShellLayout — wraps the standalone organizer / trust / admin pages
 * that live OUTSIDE the WorkspaceShell `*` catch-all.
 *
 * Two responsibilities today:
 *   1. Mount the global Cmd+K command palette so navigation works on
 *      every standalone surface (not just the workspace shell).
 *   2. Render a minimal page chrome (back-to-workspace shortcut + the
 *      page contents) so operators always have a way back to "/".
 *
 * The WorkspaceShell mounts its OWN CommandPalette instance so the
 * shell route doesn't depend on this wrapper. The two instances both
 * register a `keydown` listener; the one inside the unmounted tree
 * is removed automatically when React unmounts ShellLayout, so there
 * is no double-fire of the Cmd+K toggle.
 */
import React from "react";
import { Outlet, useNavigate } from "react-router-dom";

import CommandPalette from "./workspace/CommandPalette";
import { buildPaletteCommands } from "./workspace/buildPaletteCommands";

export default function ShellLayout(): React.ReactElement {
  const navigate = useNavigate();
  const commands = React.useMemo(() => buildPaletteCommands(navigate), [navigate]);

  return (
    <div
      className="flex flex-col h-screen"
      style={{ backgroundColor: "var(--c-bg)", color: "var(--c-text)" }}
    >
      {/* Minimal top strip — Back-to-workspace + page-id hint. The full
          TopBar lives inside WorkspaceShell; standalone routes get a
          trimmed header so the page contents have maximum vertical
          space. */}
      <header
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{
          borderColor: "var(--c-border)",
          backgroundColor: "var(--c-surface)",
        }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm rounded-md px-2 py-1 transition-colors"
          style={{ color: "var(--c-text-secondary)" }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = "var(--c-hover)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = "";
          }}
          aria-label="Back to workspace"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M10 3L5 8l5 5" />
          </svg>
          Workspace
        </button>
        <span
          className="text-xs"
          style={{ color: "var(--c-text-faint)" }}
          aria-hidden
        >
          Press Cmd+K to navigate
        </span>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <Outlet />
      </div>

      <CommandPalette commands={commands} />
    </div>
  );
}
